import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Download, Loader2, ChevronDown, Package, Filter, X, BarChart3, TrendingUp,
  ArrowUpDown, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Minus
} from 'lucide-react';

const API = 'https://api.glowytics.com/api/bq-analytics';

const MONTH_PRESETS = [
  { label: 'This Month', key: 'thisMonth' },
  { label: 'Last 3 Months', key: 'last3' },
  { label: 'Last 6 Months', key: 'last6' },
  { label: 'Last 12 Months', key: 'last12' },
  { label: 'This Year', key: 'thisYear' },
  { label: 'All Time', key: 'all' },
];

const WEEK_PRESETS = [
  { label: 'This Week', key: 'thisWeek' },
  { label: 'Last 4 Weeks', key: 'last4w' },
  { label: 'Last 8 Weeks', key: 'last8w' },
  { label: 'Last 13 Weeks', key: 'last13w' },
  { label: 'Last 26 Weeks', key: 'last26w' },
  { label: 'Last 52 Weeks', key: 'last52w' },
];

const DEFAULT_PRESET = { month: 'last6', week: 'last13w' };

// Yerel tarih → YYYY-MM-DD. toISOString() kullanma: TZ kayması bir gün geri atar.
const fmtDate = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Verilen tarihin içinde bulunduğu ISO haftasının Pazartesi'si
function mondayOf(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = (d.getDay() + 6) % 7; // Pzt = 0
  d.setDate(d.getDate() - dow);
  return d;
}

function weeksBack(n) {
  const s = mondayOf(new Date());
  s.setDate(s.getDate() - (n - 1) * 7);
  return s;
}

function getDateRange(key) {
  const today = new Date();
  const fmt = fmtDate;
  switch (key) {
    case 'thisMonth': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'last3': {
      const s = new Date(today); s.setMonth(s.getMonth() - 3);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'last6': {
      const s = new Date(today); s.setMonth(s.getMonth() - 6);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'last12': {
      const s = new Date(today); s.setMonth(s.getMonth() - 12);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'thisYear': {
      return { startDate: `${today.getFullYear()}-01-01`, endDate: fmt(today) };
    }
    // Hafta aralıkları her zaman Pazartesi'de başlar → ilk sütun yarım hafta olmaz
    case 'thisWeek': return { startDate: fmt(mondayOf(today)), endDate: fmt(today) };
    case 'last4w': return { startDate: fmt(weeksBack(4)), endDate: fmt(today) };
    case 'last8w': return { startDate: fmt(weeksBack(8)), endDate: fmt(today) };
    case 'last13w': return { startDate: fmt(weeksBack(13)), endDate: fmt(today) };
    case 'last26w': return { startDate: fmt(weeksBack(26)), endDate: fmt(today) };
    case 'last52w': return { startDate: fmt(weeksBack(52)), endDate: fmt(today) };
    default:
      return { startDate: '2024-01-01', endDate: fmt(today) };
  }
}

function fmtNum(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('tr-TR');
}

function fmtCur(n) {
  if (n == null) return '₺0';
  return '₺' + Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const MONTH_NAMES = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

// ISO hafta anahtarının (2026-W31) Pazartesi tarihi
function isoWeekMonday(isoWeek) {
  const [y, w] = isoWeek.split('-W').map(Number);
  const jan4 = new Date(y, 0, 4); // 4 Ocak her zaman ISO 1. haftadadır
  const d = mondayOf(jan4);
  d.setDate(d.getDate() + (w - 1) * 7);
  return d;
}

// Dönem anahtarını etikete çevirir: '2026-07' → 'Tem 26', '2026-W31' → 'H31 · 27 Tem'
function periodLabel(key) {
  if (!key) return '';
  if (key.includes('-W')) {
    const [, w] = key.split('-W');
    const mon = isoWeekMonday(key);
    return `H${parseInt(w, 10)} · ${mon.getDate()} ${MONTH_NAMES[mon.getMonth()]}`;
  }
  const [y, m] = key.split('-');
  return `${MONTH_NAMES[parseInt(m) - 1]} ${y.slice(2)}`;
}

function pctChange(current, previous) {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

function MoMBadge({ current, previous }) {
  if (previous == null || previous === 0) {
    if (!current) return null;
    return <span className="text-[9px] text-emerald-500 font-medium">NEW</span>;
  }
  const pct = pctChange(current, previous);
  if (Math.abs(pct) < 0.5) return <Minus className="w-2.5 h-2.5 text-gray-300 inline" />;
  const isUp = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
      {isUp ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

async function apiFetch(endpoint, params = {}) {
  const brandId = localStorage.getItem('currentBrandId');
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API}/${endpoint}?${qs}`, {
    credentials: 'include',
    headers: { 'X-Brand-ID': brandId || '' },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

function downloadCSV(rows, months, filename) {
  if (!rows?.length) return;
  const header = ['Product', 'Brand', 'Category', 'Subcategory', 'Variant',
    ...months.flatMap(m => [`${m} Qty`, `${m} Revenue`]),
    'Total Qty', 'Total Revenue'
  ];
  const csvRows = rows.map(r => [
    r.itemName, r.itemBrand, r.itemCategory, r.itemCategory2 || '', r.itemVariant,
    ...months.flatMap(m => [r.monthly[m]?.qty || 0, r.monthly[m]?.revenue || 0]),
    r.totalQty, r.totalRevenue,
  ]);
  const csv = [header.join(','), ...csvRows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const PAGE_SIZE = 50;

export default function BQItemSalesReport() {
  const [granularity, setGranularity] = useState('month'); // 'month' | 'week'
  const [preset, setPreset] = useState(DEFAULT_PRESET.month);
  const [view, setView] = useState('product');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [adsData, setAdsData] = useState(null);
  const [brandFilter, setBrandFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState('totalRevenue');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [groupBy, setGroupBy] = useState('brand');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(preset);
      const params = { startDate, endDate, granularity };
      if (brandFilter) params.brand = brandFilter;
      if (catFilter) params.category = catFilter;
      if (search) params.search = search;

      const [itemRes, grpRes, adsRes] = await Promise.all([
        apiFetch('item-sales', params),
        apiFetch('item-sales-by-brand', { startDate, endDate, granularity, groupBy }),
        groupBy === 'brand'
          ? apiFetch('brand-ads-spend', { startDate, endDate, granularity })
          : Promise.resolve(null),
      ]);
      setData(itemRes);
      setBrandData(grpRes);
      setAdsData(adsRes);
    } catch (e) {
      console.error('Failed to load item sales:', e);
    } finally {
      setLoading(false);
    }
  }, [preset, granularity, brandFilter, catFilter, search, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [brandFilter, catFilter, search, sortField, sortDir]);

  const isWeekly = granularity === 'week';
  const presets = isWeekly ? WEEK_PRESETS : MONTH_PRESETS;
  const periodWord = isWeekly ? 'week' : 'month';
  const changeLabel = isWeekly ? 'WoW' : 'MoM';

  const changeGranularity = (g) => {
    if (g === granularity) return;
    setGranularity(g);
    setPreset(DEFAULT_PRESET[g]); // hafta/ay preset anahtarları ortak değil
    setPage(0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => {
      const mul = sortDir === 'desc' ? -1 : 1;
      if (sortField === 'name') return mul * (a.itemName || '').localeCompare(b.itemName || '');
      return mul * ((a[sortField] || 0) - (b[sortField] || 0));
    });
  }, [data, sortField, sortDir]);

  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);
  const pageRows = sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const months = data?.months || [];
  const grandTotals = useMemo(() => {
    if (!sortedRows.length) return { qty: 0, revenue: 0 };
    return sortedRows.reduce((acc, r) => ({
      qty: acc.qty + r.totalQty,
      revenue: acc.revenue + r.totalRevenue,
    }), { qty: 0, revenue: 0 });
  }, [sortedRows]);

  const SortIcon = ({ field }) => (
    <button onClick={() => toggleSort(field)} className="ml-1 inline-flex text-gray-400 hover:text-gray-700">
      <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-indigo-600' : ''}`} />
    </button>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <Package className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Product Sales Report</h1>
              <p className="text-sm text-gray-500">BigQuery GA4 item-level sales data</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Kırılım: Aylık / Haftalık */}
            <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5 mr-1">
              {[{ key: 'month', label: 'Monthly' }, { key: 'week', label: 'Weekly' }].map(g => (
                <button
                  key={g.key}
                  onClick={() => changeGranularity(g.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    granularity === g.key
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            {presets.map(p => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  preset === p.key
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* View toggle + Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {/* View tabs */}
          <div className="flex bg-white rounded-lg border border-gray-200 p-1 gap-1">
            {[
              { key: 'product', label: 'Product View', icon: Package },
              { key: 'brand', label: 'Brand Summary', icon: BarChart3 },
              { key: 'category', label: 'Category Summary', icon: TrendingUp },
              { key: 'subcategory', label: 'Subcategory Summary', icon: TrendingUp },
            ].map(v => (
              <button
                key={v.key}
                onClick={() => {
                  setView(v.key);
                  if (v.key === 'brand') setGroupBy('brand');
                  if (v.key === 'category') setGroupBy('category');
                  if (v.key === 'subcategory') setGroupBy('subcategory');
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  view === v.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <v.icon className="w-3.5 h-3.5" />
                {v.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          {view === 'product' && (
            <div className="flex items-center gap-2 flex-wrap flex-1">
              <div className="flex items-center gap-1.5 text-gray-400">
                <Filter className="w-4 h-4" />
              </div>

              {data?.brands?.length > 0 && (
                <div className="relative">
                  <select
                    value={brandFilter}
                    onChange={e => setBrandFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-xs font-medium text-gray-700 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">All Brands</option>
                    {data.brands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
              )}

              {data?.categories?.length > 0 && (
                <div className="relative">
                  <select
                    value={catFilter}
                    onChange={e => setCatFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-xs font-medium text-gray-700 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">All Categories</option>
                    {data.categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
              )}

              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search product..."
                  className="pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg w-48 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                {search && (
                  <button type="button" onClick={() => { setSearch(''); setSearchInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2">
                    <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </form>

              {(brandFilter || catFilter || search) && (
                <button
                  onClick={() => { setBrandFilter(''); setCatFilter(''); setSearch(''); setSearchInput(''); }}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Clear All
                </button>
              )}

              <div className="ml-auto">
                <button
                  onClick={() => downloadCSV(sortedRows, months, `product-sales-${granularity}ly.csv`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:border-indigo-300 text-gray-700"
                >
                  <Download className="w-3.5 h-3.5" />
                  CSV
                </button>
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        )}

        {/* PRODUCT VIEW */}
        {!loading && view === 'product' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="sticky left-0 z-10 bg-gray-50 text-left px-4 py-3 font-semibold text-gray-600 min-w-[280px]">
                      Product <SortIcon field="name" />
                    </th>
                    <th className="text-left px-3 py-3 font-semibold text-gray-600 min-w-[80px]">Brand</th>
                    <th className="text-left px-3 py-3 font-semibold text-gray-600 min-w-[80px]">Category</th>
                    <th className="text-left px-3 py-3 font-semibold text-gray-600 min-w-[120px]">Subcategory</th>
                    <th className="text-left px-3 py-3 font-semibold text-gray-600 min-w-[70px]">Variant</th>
                    {months.map(m => (
                      <th key={m} className="text-center px-2 py-3 font-semibold text-gray-600 min-w-[100px]" colSpan={2}>
                        {periodLabel(m)}
                      </th>
                    ))}
                    <th className="text-right px-3 py-3 font-semibold text-gray-900 min-w-[60px] bg-indigo-50/50">
                      Total Qty <SortIcon field="totalQty" />
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-900 min-w-[90px] bg-indigo-50/50">
                      Total Rev <SortIcon field="totalRevenue" />
                    </th>
                  </tr>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th colSpan={5}></th>
                    {months.map(m => (
                      <React.Fragment key={m}>
                        <th className="text-center px-1 py-1 text-[10px] font-medium text-gray-400">Qty</th>
                        <th className="text-center px-1 py-1 text-[10px] font-medium text-gray-400">Rev</th>
                      </React.Fragment>
                    ))}
                    <th colSpan={2}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageRows.map((r, i) => (
                    <tr key={`${r.itemId}-${r.itemVariant}-${i}`} className="hover:bg-indigo-50/30 transition-colors">
                      <td className="sticky left-0 z-10 bg-white px-4 py-2.5 font-medium text-gray-900 truncate max-w-[280px]" title={r.itemName}>
                        <span className="text-gray-400 text-[10px] mr-1.5">{page * PAGE_SIZE + i + 1}.</span>
                        {r.itemName || r.itemId}
                        <span className="block text-[10px] text-gray-400 font-normal">{r.itemId}</span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-600">{r.itemBrand}</td>
                      <td className="px-3 py-2.5 text-gray-600">{r.itemCategory}</td>
                      <td className="px-3 py-2.5 text-gray-600">{r.itemCategory2 || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-500">{r.itemVariant || '—'}</td>
                      {months.map(m => {
                        const d = r.monthly[m];
                        return (
                          <React.Fragment key={m}>
                            <td className="text-center px-1 py-2.5 text-gray-700 tabular-nums">
                              {d?.qty ? fmtNum(d.qty) : <span className="text-gray-200">—</span>}
                            </td>
                            <td className="text-center px-1 py-2.5 text-gray-700 tabular-nums">
                              {d?.revenue ? fmtCur(d.revenue) : <span className="text-gray-200">—</span>}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className="text-right px-3 py-2.5 font-bold text-gray-900 bg-indigo-50/30 tabular-nums">
                        {fmtNum(r.totalQty)}
                      </td>
                      <td className="text-right px-4 py-2.5 font-bold text-gray-900 bg-indigo-50/30 tabular-nums">
                        {fmtCur(r.totalRevenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {sortedRows.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                      <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-gray-900" colSpan={5}>
                        Grand Total ({fmtNum(sortedRows.length)} products)
                      </td>
                      {months.map(m => {
                        const mTotals = sortedRows.reduce((acc, r) => ({
                          qty: acc.qty + (r.monthly[m]?.qty || 0),
                          rev: acc.rev + (r.monthly[m]?.revenue || 0),
                        }), { qty: 0, rev: 0 });
                        return (
                          <React.Fragment key={m}>
                            <td className="text-center px-1 py-3 font-bold text-gray-900 tabular-nums">{fmtNum(mTotals.qty)}</td>
                            <td className="text-center px-1 py-3 font-bold text-gray-900 tabular-nums">{fmtCur(mTotals.rev)}</td>
                          </React.Fragment>
                        );
                      })}
                      <td className="text-right px-3 py-3 font-black text-indigo-700 bg-indigo-50/50 tabular-nums">{fmtNum(grandTotals.qty)}</td>
                      <td className="text-right px-4 py-3 font-black text-indigo-700 bg-indigo-50/50 tabular-nums">{fmtCur(grandTotals.revenue)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50/50">
                <p className="text-xs text-gray-500">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sortedRows.length)} of {fmtNum(sortedRows.length)}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-1.5 rounded-md border border-gray-200 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-3 py-1 text-xs font-medium text-gray-700">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-1.5 rounded-md border border-gray-200 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {!loading && sortedRows.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No product sales data found</p>
                <p className="text-xs mt-1">Try adjusting the date range or filters</p>
              </div>
            )}
          </div>
        )}

        {/* BRAND / CATEGORY / SUBCATEGORY SUMMARY VIEW — Monthly Comparison */}
        {!loading && (view === 'brand' || view === 'category' || view === 'subcategory') && brandData && (() => {
          const showAds = view === 'brand' && adsData;
          const baseMonths = brandData.months || [];
          // Union of months from sales + ads (brand view only)
          const bMonths = showAds
            ? Array.from(new Set([...baseMonths, ...(adsData.months || [])])).sort()
            : baseMonths;

          // Merge ads spend into brand rows (case-insensitive label match).
          // Append rows that exist only in ads (e.g. "Other", or brands with
          // spend but no sales in range).
          let bRows = (brandData.rows || []).map(r => ({ ...r, monthly: { ...r.monthly } }));
          if (showAds) {
            const byKey = new Map(bRows.map(r => [String(r.label || '').toLowerCase(), r]));
            for (const ar of (adsData.rows || [])) {
              const key = String(ar.label || '').toLowerCase();
              let target = byKey.get(key);
              if (!target) {
                target = { label: ar.label, totalQty: 0, totalRevenue: 0, monthly: {} };
                bRows.push(target);
                byKey.set(key, target);
              }
              target.totalGoogleSpend = ar.totalGoogleSpend || 0;
              target.totalMetaSpend = ar.totalMetaSpend || 0;
              for (const [m, d] of Object.entries(ar.monthly || {})) {
                target.monthly[m] = {
                  qty: target.monthly[m]?.qty || 0,
                  revenue: target.monthly[m]?.revenue || 0,
                  products: target.monthly[m]?.products,
                  googleSpend: d.googleSpend || 0,
                  metaSpend: d.metaSpend || 0,
                };
              }
            }
            // Keep "Other" at the bottom, otherwise sort by total revenue desc
            bRows = bRows.sort((a, b) => {
              if (a.label === 'Other') return 1;
              if (b.label === 'Other') return -1;
              return (b.totalRevenue || 0) - (a.totalRevenue || 0);
            });
          }

          // Pre-compute monthly column totals
          const colTotals = bMonths.map((m, mi) => {
            const qty = bRows.reduce((s, r) => s + (r.monthly?.[m]?.qty || 0), 0);
            const rev = bRows.reduce((s, r) => s + (r.monthly?.[m]?.revenue || 0), 0);
            const goog = bRows.reduce((s, r) => s + (r.monthly?.[m]?.googleSpend || 0), 0);
            const meta = bRows.reduce((s, r) => s + (r.monthly?.[m]?.metaSpend || 0), 0);
            const prevM = mi > 0 ? bMonths[mi - 1] : null;
            const prevQty = prevM ? bRows.reduce((s, r) => s + (r.monthly?.[prevM]?.qty || 0), 0) : null;
            const prevRev = prevM ? bRows.reduce((s, r) => s + (r.monthly?.[prevM]?.revenue || 0), 0) : null;
            return { qty, rev, prevQty, prevRev, goog, meta };
          });
          const totalGoogle = bRows.reduce((s, r) => s + (r.totalGoogleSpend || 0), 0);
          const totalMeta = bRows.reduce((s, r) => s + (r.totalMetaSpend || 0), 0);
          const subColsPerMonth = showAds ? 5 : 3;
          return (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* CSV download for brand summary */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/30">
              <span className="text-xs font-medium text-gray-500">
                {bRows.length} {view === 'brand' ? 'brand' : view === 'subcategory' ? 'subcategory' : 'category'}(s) &middot; {bMonths.length} {periodWord}(s)
              </span>
              <button
                onClick={() => {
                  if (!bRows.length) return;
                  const header = [view === 'brand' ? 'Brand' : view === 'subcategory' ? 'Subcategory' : 'Category',
                    ...bMonths.flatMap(m => showAds
                      ? [`${periodLabel(m)} Qty`, `${periodLabel(m)} Revenue`, `${periodLabel(m)} Google Spend`, `${periodLabel(m)} Meta Spend`]
                      : [`${periodLabel(m)} Qty`, `${periodLabel(m)} Revenue`]),
                    'Total Qty', 'Total Revenue',
                    ...(showAds ? ['Total Google Spend', 'Total Meta Spend'] : []),
                  ];
                  const csvRows = bRows.map(r => [
                    r.label,
                    ...bMonths.flatMap(m => showAds
                      ? [r.monthly?.[m]?.qty || 0, r.monthly?.[m]?.revenue || 0, r.monthly?.[m]?.googleSpend || 0, r.monthly?.[m]?.metaSpend || 0]
                      : [r.monthly?.[m]?.qty || 0, r.monthly?.[m]?.revenue || 0]),
                    r.totalQty || 0, r.totalRevenue || 0,
                    ...(showAds ? [r.totalGoogleSpend || 0, r.totalMetaSpend || 0] : []),
                  ]);
                  const csv = [header.join(','), ...csvRows.map(row => row.map(v => `"${v}"`).join(','))].join('\n');
                  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `${view}-summary-${granularity}ly.csv`; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:border-indigo-300 text-gray-700"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  {/* Month header row */}
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="sticky left-0 z-10 bg-gray-50 text-left px-4 py-3 font-semibold text-gray-600 min-w-[180px]" rowSpan={2}>
                      {view === 'brand' ? 'Brand' : view === 'subcategory' ? 'Subcategory' : 'Category'}
                    </th>
                    {bMonths.map((m, mi) => (
                      <th
                        key={m}
                        className={`text-center px-1 py-2.5 font-bold text-gray-700 ${showAds ? 'min-w-[260px]' : 'min-w-[150px]'} ${mi % 2 === 0 ? 'bg-gray-50' : 'bg-slate-50'}`}
                        colSpan={subColsPerMonth}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-[11px]">{periodLabel(m)}</span>
                          {mi > 0 && colTotals[mi] && (
                            <MoMBadge current={colTotals[mi].rev} previous={colTotals[mi].prevRev} />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="text-right px-3 py-3 font-semibold text-gray-900 bg-indigo-50/50 min-w-[70px]" rowSpan={2}>
                      <div className="flex flex-col items-end">
                        <span>Total</span>
                        <span className="text-[10px] font-medium text-gray-400">Qty</span>
                      </div>
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-900 bg-indigo-50/50 min-w-[100px]" rowSpan={2}>
                      <div className="flex flex-col items-end">
                        <span>Total</span>
                        <span className="text-[10px] font-medium text-gray-400">Revenue</span>
                      </div>
                    </th>
                    {showAds && (
                      <>
                        <th className="text-right px-3 py-3 font-semibold text-gray-900 bg-amber-50/70 min-w-[90px]" rowSpan={2}>
                          <div className="flex flex-col items-end">
                            <span>Total</span>
                            <span className="text-[10px] font-medium text-gray-400">Google</span>
                          </div>
                        </th>
                        <th className="text-right px-3 py-3 font-semibold text-gray-900 bg-sky-50/70 min-w-[90px]" rowSpan={2}>
                          <div className="flex flex-col items-end">
                            <span>Total</span>
                            <span className="text-[10px] font-medium text-gray-400">Meta</span>
                          </div>
                        </th>
                      </>
                    )}
                  </tr>
                  {/* Sub-header: Qty / Revenue / (Google / Meta) / MoM */}
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    {bMonths.map((m, mi) => (
                      <React.Fragment key={m}>
                        <th className={`text-center px-1 py-1 text-[10px] font-medium text-gray-400 ${mi % 2 === 0 ? 'bg-gray-50' : 'bg-slate-50'}`}>Qty</th>
                        <th className={`text-center px-1 py-1 text-[10px] font-medium text-gray-400 ${mi % 2 === 0 ? 'bg-gray-50' : 'bg-slate-50'}`}>Revenue</th>
                        {showAds && (
                          <>
                            <th className={`text-center px-1 py-1 text-[10px] font-medium text-amber-600 ${mi % 2 === 0 ? 'bg-amber-50/40' : 'bg-amber-50/60'}`}>Google</th>
                            <th className={`text-center px-1 py-1 text-[10px] font-medium text-sky-600 ${mi % 2 === 0 ? 'bg-sky-50/40' : 'bg-sky-50/60'}`}>Meta</th>
                          </>
                        )}
                        <th className={`text-center px-1 py-1 text-[10px] font-medium text-gray-400 ${mi % 2 === 0 ? 'bg-gray-50' : 'bg-slate-50'}`}>
                          {mi > 0 ? changeLabel : ''}
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {bRows.map((r, i) => (
                    <tr key={r.label || i} className={`hover:bg-indigo-50/30 transition-colors ${r.label === 'Other' ? 'bg-gray-50/50 italic' : ''}`}>
                      <td className={`sticky left-0 z-10 px-4 py-3 font-semibold text-gray-900 ${r.label === 'Other' ? 'bg-gray-50/50' : 'bg-white'}`}>{r.label || '—'}</td>
                      {bMonths.map((m, mi) => {
                        const d = r.monthly?.[m];
                        const prevM = mi > 0 ? bMonths[mi - 1] : null;
                        const prevD = prevM ? r.monthly?.[prevM] : null;
                        const bgClass = mi % 2 === 0 ? '' : 'bg-slate-50/30';
                        return (
                          <React.Fragment key={m}>
                            <td className={`text-center px-1 py-3 text-gray-700 tabular-nums ${bgClass}`}>
                              {d?.qty ? fmtNum(d.qty) : <span className="text-gray-200">—</span>}
                            </td>
                            <td className={`text-center px-1 py-3 text-gray-700 tabular-nums ${bgClass}`}>
                              {d?.revenue ? fmtCur(d.revenue) : <span className="text-gray-200">—</span>}
                            </td>
                            {showAds && (
                              <>
                                <td className={`text-center px-1 py-3 text-amber-700 tabular-nums ${mi % 2 === 0 ? 'bg-amber-50/30' : 'bg-amber-50/50'}`}>
                                  {d?.googleSpend ? fmtCur(d.googleSpend) : <span className="text-gray-200">—</span>}
                                </td>
                                <td className={`text-center px-1 py-3 text-sky-700 tabular-nums ${mi % 2 === 0 ? 'bg-sky-50/30' : 'bg-sky-50/50'}`}>
                                  {d?.metaSpend ? fmtCur(d.metaSpend) : <span className="text-gray-200">—</span>}
                                </td>
                              </>
                            )}
                            <td className={`text-center px-1 py-3 ${bgClass}`}>
                              {mi > 0 ? <MoMBadge current={d?.revenue || 0} previous={prevD?.revenue || 0} /> : null}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className="text-right px-3 py-3 font-bold text-gray-900 bg-indigo-50/30 tabular-nums">{fmtNum(r.totalQty)}</td>
                      <td className="text-right px-4 py-3 font-bold text-gray-900 bg-indigo-50/30 tabular-nums">{fmtCur(r.totalRevenue)}</td>
                      {showAds && (
                        <>
                          <td className="text-right px-3 py-3 font-bold text-amber-800 bg-amber-50/60 tabular-nums">
                            {r.totalGoogleSpend ? fmtCur(r.totalGoogleSpend) : <span className="text-gray-200">—</span>}
                          </td>
                          <td className="text-right px-3 py-3 font-bold text-sky-800 bg-sky-50/60 tabular-nums">
                            {r.totalMetaSpend ? fmtCur(r.totalMetaSpend) : <span className="text-gray-200">—</span>}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
                {bRows.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                      <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-gray-900">Grand Total</td>
                      {bMonths.map((m, mi) => {
                        const ct = colTotals[mi];
                        const bgClass = mi % 2 === 0 ? 'bg-gray-50' : 'bg-slate-50';
                        return (
                          <React.Fragment key={m}>
                            <td className={`text-center px-1 py-3 font-bold text-gray-900 tabular-nums ${bgClass}`}>{fmtNum(ct.qty)}</td>
                            <td className={`text-center px-1 py-3 font-bold text-gray-900 tabular-nums ${bgClass}`}>{fmtCur(ct.rev)}</td>
                            {showAds && (
                              <>
                                <td className={`text-center px-1 py-3 font-bold text-amber-800 tabular-nums ${mi % 2 === 0 ? 'bg-amber-50/60' : 'bg-amber-50/80'}`}>{fmtCur(ct.goog)}</td>
                                <td className={`text-center px-1 py-3 font-bold text-sky-800 tabular-nums ${mi % 2 === 0 ? 'bg-sky-50/60' : 'bg-sky-50/80'}`}>{fmtCur(ct.meta)}</td>
                              </>
                            )}
                            <td className={`text-center px-1 py-3 ${bgClass}`}>
                              {mi > 0 ? <MoMBadge current={ct.rev} previous={ct.prevRev} /> : null}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className="text-right px-3 py-3 font-black text-indigo-700 bg-indigo-50/50 tabular-nums">
                        {fmtNum(bRows.reduce((s, r) => s + r.totalQty, 0))}
                      </td>
                      <td className="text-right px-4 py-3 font-black text-indigo-700 bg-indigo-50/50 tabular-nums">
                        {fmtCur(bRows.reduce((s, r) => s + r.totalRevenue, 0))}
                      </td>
                      {showAds && (
                        <>
                          <td className="text-right px-3 py-3 font-black text-amber-800 bg-amber-100/70 tabular-nums">{fmtCur(totalGoogle)}</td>
                          <td className="text-right px-3 py-3 font-black text-sky-800 bg-sky-100/70 tabular-nums">{fmtCur(totalMeta)}</td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {!loading && bRows.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No sales data found</p>
              </div>
            )}
          </div>
          );
        })()}
      </div>
    </div>
  );
}
